import { useState, useCallback } from "react";
import { collectionApi } from "src/common";
import { usePostUsersMutation, usePostUsersVerifyMutation } from "src/common/api/collection/collection-rtk-api";
import { CryptoAPI } from "src/common/api/crypto/crypto-api";

interface UseCreateUserResult {
  createUser: (user_id: string, display_name: string) => Promise<void>;
  success: boolean;
  setSuccess: (success: boolean) => void;
}

// Description: Create a new user and setup their account
export function useCreateAccount(): UseCreateUserResult {
  const [success, setSuccess] = useState(false);
  const [updateUser] = usePostUsersMutation();
  const [verifyUser] = usePostUsersVerifyMutation();
  const [trigger] = collectionApi.endpoints.getTestUsersSecret.useLazyQuery();

  const createUser = useCallback(async (user_id: string, display_name: string): Promise<void> => {
    await updateUser({ user_id, display_name }).unwrap();
    const { secret } = await trigger({ userId: user_id, secretType: "account_setup" }).unwrap();

    // NOTE: Convert base64 to binary string and then to decimal
    const binary_str = atob(secret);
    const binary_array = Array.from(binary_str, char => {
      const bin = char.charCodeAt(0).toString(2);
      return bin.padStart(8, "0");
    });
    const full_binary = binary_array.join("");
    const decimal = BigInt("0b" + full_binary);
    const six_digits_secret = decimal.toString();

    // NOTE: Need to verify the user with the six digit secret before setting up their account
    const verify_response = await verifyUser({ user_id, secret: six_digits_secret }).unwrap();
    const data = {
      user_id,
      secret,
      key_version: verify_response.key_version,
      display_name,
      account_claim_receipt: verify_response.account_claim_receipt,
      org_name: verify_response.org_name,
    };

    await CryptoAPI.setupNewAccount(data);
  }, [updateUser, verifyUser, trigger]);

  return { createUser, success, setSuccess };
} 
